import { makeAutoObservable, runInAction, onBecomeObserved, toJS } from 'mobx'
import { SegmentService } from '../services/segmentService'
import { SegmentDto, SegmentReach, SegmentStatus, Device } from '../types'
import { AdStatus } from '../types/AdPlatforms'
import { ToastType } from '../types/ToastData'
import { RootStore } from './rootStore'
import { adminService } from '../services/adminService'
import {
    BasePubmaticSegment,
    CategoryItem,
    FrequencyCap,
    ImpressionTracker,
    Retargeting,
    RetargetingDefinition,
    Segment,
    SegmentType,
    TargetingDto
} from '../types/Segment'
import { Adtech } from '../types/Pricing'
import { HttpStatusCode } from '../services/types'
import { downloadFile } from '../utils/download'
import { InterestScore } from '../utils/scoreHelpers'
import { isProduction } from '../config'
import { InsightsService } from '../services/insightsService'

type CurrentSegment = {
    id?: number
    companyId?: number
    status: SegmentStatus
    size: number
    targeting?: TargetingDto
    retargeting?: Retargeting
    durationDays: number
    reportUrl?: string
    segmentData?: Record<string, any>
    type: SegmentType
    config?: {
        gam?: {
            ppsEnabled?: boolean
        }
    }
}

export class SegmentStore {
    private segmentService: SegmentService
    private insightsService: InsightsService
    private rootStore: RootStore

    languages: string[] = []
    categories: CategoryItem[] = []
    osList: Device[] | null = null
    segments: Segment[] = []
    segmentsReach: SegmentReach[] = []
    locations: any = null
    error: string | null = null
    equativSegment: boolean = false
    xandrCurateSegment: boolean = false
    xandrMonetizeSegment: boolean = false
    criteoSegment: boolean = false
    gamSegment: boolean = false
    oneTagSegment: boolean = true
    pubmaticSegment: BasePubmaticSegment | null = null
    isBeingPublished: boolean = false
    maxUsersNumber = 0
    allSegments: any[] = []
    currentSegment?: CurrentSegment | null = null
    adminSegment: any = null
    lookalikeCategories: Record<string, number> | null = null
    impressionTracker: ImpressionTracker | null = null

    constructor(rootStore: RootStore, segmentService: SegmentService, insightsService: InsightsService) {
        this.segmentService = segmentService
        this.insightsService = insightsService
        this.rootStore = rootStore

        makeAutoObservable(this)
        this.resetCurrentSegment()

        onBecomeObserved(this, 'languages', async () => {
            if (this.languages.length > 0) return
            const res = await this.segmentService.getLanguages()
            runInAction(() => {
                const brokenLanguages = [
                    'ace',
                    'ach',
                    'ckb',
                    'enus',
                    'hr',
                    'kpe',
                    'la',
                    'lij',
                    'mo',
                    'other',
                    'sat',
                    'sc',
                    'scn',
                    'sm',
                    'tl',
                    'und',
                    'us',
                    'yue',
                    'zz',
                    'an',
                    'apw',
                    'eng',
                    'gb',
                    'sh'
                ]
                this.languages = res ? res.filter((x) => !brokenLanguages.includes(x)) : []
            })
        })
        onBecomeObserved(this, 'categories', async () => {
            if (this.categories.length > 0) return
            const res = await this.segmentService.getCategories()
            runInAction(() => {
                this.categories = res || []
            })
        })
        onBecomeObserved(this, 'osList', async () => {
            if (this.osList) return
            const res = await this.segmentService.getOsList()
            runInAction(() => {
                this.osList = res
            })
        })
        onBecomeObserved(this, 'segments', async () => {
            const res = await this.segmentService.getAll()

            runInAction(() => {
                this.rootStore.appStore.incrementRequests()
                this.segments = res ? res : []
                this.rootStore.appStore.decrementRequests()
            })
        })
        onBecomeObserved(this, 'segmentsReach', async () => {
            if (this.segmentsReach.length > 0) return
            const res = this.rootStore.companyStore.isDemo ? await this.segmentService.getDemoReach() : await this.segmentService.getReach()

            runInAction(() => {
                this.rootStore.appStore.incrementRequests()
                this.segmentsReach = res
                this.rootStore.appStore.decrementRequests()
            })
        })
        onBecomeObserved(this, 'locations', async () => {
            if (this.locations) return
            const res = await this.segmentService.getLocations()
            runInAction(() => {
                this.locations = res
            })
        })
        onBecomeObserved(this, 'maxUsersNumber', async () => {
            if (this.maxUsersNumber) return

            const dto = this.mapCurrentToSegmentDto()
            dto.name = 'test'
            dto.companyId = this.currentSegment?.companyId || this.rootStore.companyStore.myCompany.company.id
            dto.targeting = {
                language: null,
                categoryIds: null,
                deviceOs: null,
                deviceBrowser: null,
                deviceType: null,
                country: null,
                city: null,
                keyword: null,
                topicIds: null,
                gender: null,
                networth: null,
                lookalikeCats: null,
                adjacentTopicIds: null,
                sizeRecency: ['180'],
                engagement: null
            }

            runInAction(() => {
                this.maxUsersNumber = dto.size
            })
        })
    }

    convertLocalDateToUTC(localDate: Date, endDate: boolean) {
        const utcDate = new Date(localDate.getTime() - localDate.getTimezoneOffset() * 60000)

        if (endDate) {
            utcDate.setUTCHours(23)
            utcDate.setUTCMinutes(59)
            utcDate.setUTCSeconds(59)
            utcDate.setUTCMilliseconds(999)
        }

        return utcDate.toISOString()
    }

    convertUTCDateToLocal(utcDate: string) {
        const localDate = new Date(utcDate)
        if (localDate.getUTCHours() > 0) {
            localDate.setUTCHours(0)
            localDate.setUTCMinutes(0)
            localDate.setUTCSeconds(0)
            localDate.setUTCMilliseconds(0)

            const offset = new Date().getTimezoneOffset() * 60 * 1000
            const localDate1 = new Date(localDate.getTime() - offset)
            localDate1.setHours(0, 0, 0, 0)

            return localDate1
        }
        if (localDate.getUTCHours() === 0) {
            const offset = new Date().getTimezoneOffset() * 60 * 1000
            const localDate2 = new Date(localDate.getTime() - offset)

            localDate2.setHours(0, 0, 0, 0)

            return localDate2
        }
    }

    resetCurrentSegment() {
        runInAction(() => {
            this.currentSegment = {
                status: SegmentStatus.Draft,
                type: SegmentType.Targeting,
                size: 0,
                durationDays: 120,
                retargeting: null,
                //  test  data to develop retargeting view
                // {
                //     definition: {
                //         properties: [
                //             {
                //                 id: 1,
                //                 value: null
                //             }
                //         ]
                //     },
                //     domains: []
                // },
                companyId: this.rootStore.companyStore?.myCompany?.company?.id,
                targeting: {
                    categoryIds: null,
                    city: null,
                    country: null,
                    deviceBrowser: null,
                    deviceOs: null,
                    deviceType: null,
                    keyword: null,
                    language: null,
                    sizeRecency: null,
                    topicIds: null,
                    gender: null,
                    networth: null,
                    lookalikeCats: null,
                    adjacentTopicIds: null,
                    engagement: null
                },
                segmentData: {
                    name: '',
                    domains: []
                }
            }

            this.setImpressionTracker(null)
        })
    }

    get segment() {
        return this.mapCurrentToSegmentDto()
    }

    private mapDtoToCurrent(dto: SegmentDto): CurrentSegment {
        const { id, companyId, status, size, durationDays, targeting, retargeting, type, config } = dto

        const res: CurrentSegment = {
            id: id,
            companyId,
            status,
            size,
            type,
            durationDays,
            targeting,
            retargeting,
            config: config || null,
            segmentData: {
                name: dto.name
            }
        }

        return res
    }

    private mapCurrentToSegmentDto() {
        const { segmentData, id, companyId, status, size, durationDays, targeting, retargeting, type, config } = this.currentSegment

        const dto: SegmentDto = {
            id,
            companyId: companyId >= 0 ? companyId : this.rootStore.companyStore?.myCompany?.company?.id,
            size,
            type,
            durationDays,
            name: segmentData.name,
            status: status,
            targeting,
            retargeting,
            config: config || null
        }
        if (retargeting) {
            delete dto.definition
        } else {
            // set default engagement if categories selected
            if (dto?.targeting?.categoryIds?.length && !dto?.targeting?.engagement) {
                dto.targeting.engagement = InterestScore.Moderate
            }

            // clear engagement if there are no selected categories
            if (!dto?.targeting?.categoryIds?.length && dto?.targeting?.engagement) {
                dto.targeting.engagement = null
            }

            delete dto.retargeting
        }
        return dto
    }
    setSegmentName = (name: string) => {
        this.currentSegment.segmentData.name = name
    }
    setSegmentFilters(data: TargetingDto) {
        this.currentSegment.targeting = data
    }
    setSegmentData(data: Record<string, string | number[]>) {
        this.currentSegment.segmentData = data
    }
    setDurationData(data: number) {
        this.currentSegment.durationDays = data
    }
    setRetargeting(data: Retargeting) {
        this.currentSegment.retargeting = data
    }
    setLookalikeCats(data: string[] | null) {
        this.currentSegment.targeting.lookalikeCats = data
    }
    setInterestScore(data: InterestScore) {
        this.currentSegment.targeting.engagement = data
    }
    setSegmentSize(data: number) {
        this.currentSegment.size = data
    }
    setRetargetingDifinition(data: RetargetingDefinition) {
        this.currentSegment.retargeting.definition = data
    }
    setType(data: SegmentType) {
        this.currentSegment.type = data
    }
    setConfig(data: { gam?: { ppsEnabled?: boolean }; public?: boolean }) {
        this.currentSegment.config = data
    }
    setDomains(data: string[]) {
        this.currentSegment.retargeting.domains = data
    }
    setCompanyId(data: number) {
        this.currentSegment.companyId = data
    }

    async getSegments() {
        runInAction(() => {
            this.error = null
        })
        try {
            this.rootStore.appStore.incrementRequests()
            const segments = await this.segmentService.getAll()
            runInAction(() => {
                this.segments = segments
            })
        } catch (error) {
            runInAction(() => {
                this.rootStore.appStore.setToastNotification({
                    type: ToastType.Error,
                    title: 'Failed to get segments'
                })
                runInAction(() => {
                    this.error = error
                })
            })
        } finally {
            this.rootStore.appStore.decrementRequests()
        }
    }

    async setCurrentSegment(id: number) {
        runInAction(() => {
            this.error = null
        })
        try {
            this.rootStore.appStore.incrementRequests()
            const segments = await this.segmentService.getAll()
            let current = segments.find((x) => x.segment.id === id)
            if (typeof current === 'undefined' && this.rootStore.authStore.isAdmin) {
                const resp = await adminService.getSegmentAdmin(id)
                runInAction(() => {
                    this.adminSegment = resp
                    this.segments = segments ? segments : []
                    current = this.adminSegment
                })
            }
            if (current) {
                runInAction(() => {
                    this.segments = segments ? segments : []
                    this.currentSegment = this.mapDtoToCurrent(current.segment)
                })
            } //TODO: else - throw 404!
        } catch (error) {
            // eslint-disable-next-line
            console.error(error)
            runInAction(() => {
                this.error = error
            })
        } finally {
            this.rootStore.appStore.decrementRequests()
        }
    }

    async save() {
        if (this.currentSegment.id) {
            this.update(false)
        }
    }

    async createEmpty(name: string, clonedSegment?: SegmentDto) {
        runInAction(() => {
            this.error = null
        })
        const dto = this.mapCurrentToSegmentDto()
        dto.name = name
        dto.segmentData = {
            name: dto.name
        }
        if (this.rootStore.appStore.isCreateRetargetingSegment) {
            dto.retargeting = {
                definition: {
                    properties: []
                }
            }
            dto.type = SegmentType.Retargeting
            delete dto.targeting
        } else {
            dto.targeting = {
                language: null,
                categoryIds: null,
                deviceOs: null,
                deviceBrowser: null,
                deviceType: null,
                country: null,
                city: null,
                keyword: null,
                topicIds: null,
                gender: null,
                networth: null,
                lookalikeCats: null,
                adjacentTopicIds: null,
                engagement: null,
                sizeRecency: ['180']
            }
        }

        if (clonedSegment) {
            const _c = toJS(clonedSegment)
            dto.targeting = _c.targeting
            dto.retargeting = _c.retargeting
            dto.type = _c.type
            dto.lookalikeCats = _c.lookalikeCats
            // we do not support 730 days anymore
            dto.durationDays = _c.durationDays === 730 ? 365 : _c.durationDays
            dto.config = { ..._c?.config }
        }

        this.currentSegment = dto
    }

    async create() {
        runInAction(() => {
            this.error = null
        })
        const dto = this.mapCurrentToSegmentDto()

        try {
            this.rootStore.appStore.incrementRequests()

            const resp = await this.segmentService.create(dto)

            runInAction(() => {
                this.currentSegment.id = resp.id
                this.currentSegment.size = resp.size
                this.currentSegment.type = resp.type
                this.currentSegment.durationDays = resp.durationDays
                this.segments.push({ prices: [], segment: resp })
                this.rootStore.appStore.setCreateRetargetingSegment(false)
            })
        } catch (error) {
            this.rootStore.appStore.setToastNotification({
                type: ToastType.Error,
                title: 'Failed to create the segment'
            })
            runInAction(() => {
                this.error = error
            })
        } finally {
            this.rootStore.appStore.decrementRequests()

            this.rootStore.appStore.setToastNotification({
                type: ToastType.Success,
                title: 'Saved',
                body: 'The segment was successfully saved',
                isHidden: true
            })
        }
    }

    private async update(hideToast: boolean, isSilent = false) {
        runInAction(() => {
            this.error = null
        })

        const dto = this.mapCurrentToSegmentDto()

        try {
            this.rootStore.appStore.incrementRequests()

            // replace empty arrays with null
            Object.keys(dto.targeting).forEach((k: keyof TargetingDto) => {
                if (Array.isArray(dto.targeting[k]) && !dto.targeting[k].length) {
                    dto.targeting[k] = null
                }
            })

            const resp = this.rootStore.authStore.isAdmin
                ? await adminService.updateSegmentAdmin(this.mapCurrentToSegmentDto())
                : await this.segmentService.update(dto)
            const index = this.segments.findIndex((x) => x.segment?.id === dto.id)
            runInAction(() => {
                this.currentSegment.size = resp.size
                this.currentSegment.durationDays = resp.durationDays
                this.segments[index] = { prices: this.segments[index].prices, segment: resp }
            })
        } catch (error) {
            runInAction(() => {
                this.error = error
            })

            // TODO: refactor the BE to have more control over errors formatting
            if (error?.message?.includes('failed: segment threshold is too low')) {
                this.showLowMauError()
            } else {
                this.rootStore.appStore.setToastNotification({
                    type: ToastType.Error,
                    title: 'Not Saved',
                    body: error?.message ? error?.message : 'The segment was not saved',
                    isHidden: hideToast
                })
            }

            this.rootStore.appStore.decrementRequests()
        } finally {
            if (!this.error) {
                !isSilent &&
                    this.rootStore.appStore.setToastNotification({
                        type: ToastType.Success,
                        title: 'Saved',
                        body: 'The segment was successfully saved',
                        isHidden: hideToast
                    })
            }

            this.rootStore.appStore.decrementRequests()
        }
    }

    showLowMauError() {
        this.rootStore.appStore.setToastNotification({
            type: ToastType.Error,
            title: 'Not Activated',
            body: `You can not create segments with estimated size less than ${isProduction && !this.rootStore.authStore.isAdmin ? 1000 : 10}`,
            isHidden: false
        })
    }

    async estimate() {
        runInAction(() => {
            this.error = null
        })

        let resp: { size: number; singleSite: number }

        try {
            this.rootStore.appStore.incrementRequests()
            this.rootStore.appStore.setIsCurrentlyEstimated(true)
            const dto = this.mapCurrentToSegmentDto()

            resp = await this.insightsService.estimateClickHouse(dto)

            runInAction(() => {
                this.currentSegment.size = resp.size
            })
        } catch (error) {
            runInAction(() => {
                this.error = error
            })
            this.rootStore.appStore.setToastNotification({
                type: ToastType.Error,
                title: 'Not Estimated',
                body: error?.message ? error.message : 'The segment was not estimated'
            })
        } finally {
            this.rootStore.appStore.decrementRequests()
            this.rootStore.appStore.setIsCurrentlyEstimated(false)
        }
    }

    async delete(id: number) {
        runInAction(() => {
            this.error = null
        })
        try {
            this.rootStore.appStore.incrementRequests()

            const index = this.segments.findIndex((x) => x?.segment.id === id)

            const companyId = index >= 0 ? this.rootStore.companyStore.myCompany.company.id : this.adminSegment.companyId

            this.rootStore.authStore.isAdmin ? await adminService.deleteSegmentAdmin(id, companyId) : await this.segmentService.delete(id)

            if (index >= 0) {
                runInAction(() => {
                    this.segments.splice(index, 1)
                })
            }
        } catch (error) {
            this.rootStore.appStore.setToastNotification({
                type: ToastType.Error,
                title: 'Failed to delete the segment'
            })
            runInAction(() => {
                this.error = error?.message || error
            })
        } finally {
            this.rootStore.appStore.decrementRequests()
        }
    }

    async archive(id: number) {
        runInAction(() => {
            this.error = null
        })
        try {
            this.rootStore.appStore.incrementRequests()
            const resp = await this.segmentService.archive(id)
            const index = this.segments.findIndex((x) => x?.segment.id === id)

            if (index >= 0) {
                runInAction(() => {
                    this.segments[index] = { prices: this.segments[index].prices, segment: resp }
                    this.currentSegment.status = resp.status
                })
            }
        } catch (error) {
            runInAction(() => {
                this.error = error
            })
        } finally {
            this.rootStore.appStore.decrementRequests()
        }
    }

    async getSegmentExportStatus(companyId: number, segment: SegmentDto, adtech: Adtech) {
        runInAction(() => {
            this.error = null
        })
        try {
            this.rootStore.appStore.incrementRequests()
            const resp = this.rootStore.authStore.isAdmin
                ? await adminService.getAdminSegmentExportStatus(companyId, segment.id, adtech)
                : await this.segmentService.getSegmentExportStatus(segment.id, adtech)
            runInAction(() => {
                switch (adtech) {
                    case Adtech.XandrCurate:
                        this.xandrCurateSegment = resp.status === AdStatus.Active
                        break
                    case Adtech.XandrMonetize:
                        this.xandrMonetizeSegment = resp.status === AdStatus.Active
                        break
                    case Adtech.Equativ:
                        this.equativSegment = resp.status === AdStatus.Active
                        break
                    case Adtech.Criteo:
                        this.criteoSegment = resp.status === AdStatus.Active
                        break
                    case Adtech.GAM:
                        this.gamSegment = resp.status === AdStatus.Active
                        break
                    case Adtech.Pubmatic:
                        this.pubmaticSegment = resp as any
                        break
                    case Adtech.Onetag:
                        this.oneTagSegment = resp.status === AdStatus.Active
                        break
                }
            })
        } catch (error) {
            runInAction(() => {
                this.error = error.message
            })
        } finally {
            this.rootStore.appStore.decrementRequests()
        }
    }

    async exportSegment(segmentId: number, adtech: Adtech) {
        runInAction(() => {
            this.error = null
        })
        try {
            this.rootStore.appStore.incrementRequests()
            await this.segmentService.exportSegment(segmentId, adtech)
        } catch (error) {
            runInAction(() => {
                this.error = error
                this.rootStore.appStore.setToastNotification({
                    type: ToastType.Error,
                    title: 'Not sent!',
                    body: 'Segment was not sent',
                    isHidden: false
                })
            })
        } finally {
            this.rootStore.appStore.decrementRequests()
            if (!this.error) {
                this.rootStore.appStore.setToastNotification({
                    type: ToastType.Success,
                    title: 'Sent!',
                    body: 'Segment was sent succesfully',
                    isHidden: false
                })
            }
        }
    }

    async publish(id: number) {
        runInAction(() => {
            this.error = null
        })
        try {
            this.rootStore.appStore.incrementRequests()
            this.isBeingPublished = true
            const resp = await this.segmentService.publish(id)
            const index = this.segments.findIndex((x) => x?.segment.id === id)
            runInAction(() => {
                this.segments[index] = { prices: this.segments[index].prices, segment: resp }
                this.currentSegment.status = resp.status
            })
            return resp
        } catch (error) {
            runInAction(() => {
                this.error = error
                this.showLowMauError()
                this.isBeingPublished = false
                this.rootStore.appStore.decrementRequests()
            })
        } finally {
            this.rootStore.appStore.decrementRequests()
            if (!this.error) {
                this.rootStore.appStore.setToastNotification({
                    type: ToastType.Activation,
                    title: 'Activated!',
                    body: 'Segment was activated succesfully',
                    isHidden: false
                })
                this.isBeingPublished = false
            }
        }
    }

    async schedule(id: number) {
        runInAction(() => {
            this.error = null
        })
        try {
            this.rootStore.appStore.incrementRequests()
            this.isBeingPublished = true
            await this.update(true)
            if (this.error) {
                throw this.error
            }
            const resp = await this.segmentService.schedule(id)
            const index = this.segments.findIndex((x) => x?.segment.id === id)
            runInAction(() => {
                this.segments[index] = { prices: this.segments[index].prices, segment: resp }
                this.currentSegment.status = resp.status
            })
            return resp
        } catch (error) {
            runInAction(() => {
                this.error = error
                this.rootStore.appStore.setToastNotification({
                    type: ToastType.Error,
                    title: 'Not Scheduled!',
                    body: 'Segment was not scheduled',
                    isHidden: false
                })
                this.isBeingPublished = false
                this.rootStore.appStore.decrementRequests()
            })
        } finally {
            this.rootStore.appStore.decrementRequests()
            if (!this.error) {
                this.rootStore.appStore.setToastNotification({
                    type: ToastType.Activation,
                    title: 'Scheduled!',
                    body: 'Segment was scheduled succesfully',
                    isHidden: false
                })
                this.isBeingPublished = false
            }
        }
    }

    async downloadPPS(id: number) {
        runInAction(() => {
            this.error = null
        })
        try {
            this.rootStore.appStore.incrementRequests()
            const res = await this.segmentService.getPPS(id)

            downloadFile(res.message, `gampps_export_${new Date().toLocaleDateString()}.csv`)
        } catch (error) {
            runInAction(() => {
                this.error = error
                this.rootStore.appStore.setToastNotification({
                    type: ToastType.Error,
                    title: 'Not Donwloaded!',
                    body: 'PPS file was not downloaded',
                    isHidden: false
                })
            })
        } finally {
            this.rootStore.appStore.decrementRequests()
        }
    }

    async downloadPPSAdmin() {
        runInAction(() => {
            this.error = null
        })
        try {
            this.rootStore.appStore.incrementRequests()
            const res = await this.segmentService.getPPSAdmin()

            downloadFile(res.message, `all_gampps_export_${new Date().toLocaleDateString()}.csv`)
        } catch (error) {
            runInAction(() => {
                this.error = error
                this.rootStore.appStore.setToastNotification({
                    type: ToastType.Error,
                    title: 'Not Donwloaded!',
                    body: 'PPS file was not downloaded',
                    isHidden: false
                })
            })
        } finally {
            this.rootStore.appStore.decrementRequests()
        }
    }

    async createImpressionTracker(id: number, freqCap: FrequencyCap[]) {
        runInAction(() => {
            this.error = null
        })
        try {
            this.rootStore.appStore.incrementRequests()
            const resp = await this.segmentService.createImpressionTracker(id, freqCap)

            runInAction(() => {
                this.setImpressionTracker(resp)
            })
            return resp
        } catch (error) {
            runInAction(() => {
                this.error = error
                this.rootStore.appStore.setToastNotification({
                    type: ToastType.Error,
                    title: 'Impression tracker was not created',
                    body: error?.message,
                    isHidden: false
                })
            })
        } finally {
            this.rootStore.appStore.decrementRequests()
            if (!this.error) {
                this.rootStore.appStore.setToastNotification({
                    type: ToastType.Activation,
                    title: 'Created!',
                    body: 'Impression tracker was created succesfully',
                    isHidden: false
                })
            }
        }
    }

    async getImpressionTracker(id: number) {
        runInAction(() => {
            this.error = null

            // clear prev value
            this.setImpressionTracker(null)
        })
        try {
            this.rootStore.appStore.incrementRequests()
            const resp = await this.segmentService.getImpressionTracker(id)

            runInAction(() => {
                this.setImpressionTracker(resp)
            })
            return resp
        } catch (error) {
            //TODO: add error types
            //'Not found' impression tracker is valid  case, do not show the error
            if (error.status !== HttpStatusCode.NotFound) {
                runInAction(() => {
                    this.error = error
                })
            }
        } finally {
            this.rootStore.appStore.decrementRequests()
        }
    }

    setImpressionTracker(value: ImpressionTracker | null) {
        this.impressionTracker = value
    }
}
